import React, { FC } from 'react';
import _ from 'lodash';
import {
  Box,
  BoxProps,
  Text,
  TextProps,
  Layout,
  Cell,
} from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { useTemplateContext } from '@components/Contexts/TemplateContext';
import { ENABLE_TEMPLATES_MULTI_LANGUAGE_SPEC } from '@utils/constants';

type IndustryAndLanguageProps = {
  direction: BoxProps['direction'];
  textSize: TextProps['size'];
};

const IndustryAndLanguage: FC<IndustryAndLanguageProps> = ({
  direction,
  textSize,
}) => {
  const {
    template: { industries },
  } = useTemplateContext();
  const { t } = useTranslation();

  return (
    <Box width="100%" direction={direction} marginTop="18px">
      <Layout gap="24px">
        <Cell span={direction === 'vertical' ? 12 : 6}>
          <Box direction="vertical" width="328px">
            <Text size={textSize} secondary>
              {t('marketplace.templates-page.about-section-industry')}
            </Text>
            <Text>
              {industries.map((id, index) => {
                return (
                  <>
                    <Text size={textSize}>
                      {t(
                        `marketplace.templates-page.industry-${_.toLower(id)}`,
                      )}
                    </Text>
                    {index !== industries.length - 1 && (
                      <Text size={textSize}>
                        {t(
                          'marketplace.templates-page.about-section-industry-comma',
                        )}
                        &nbsp;
                      </Text>
                    )}
                  </>
                );
              })}
            </Text>
          </Box>
        </Cell>
        <Cell span={direction === 'vertical' ? 12 : 6}>
          <Box direction="vertical" maxWidth="328px" width="100%">
            <Text size={textSize} secondary>
              {t('marketplace.templates-page.about-section-language')}
            </Text>
            <TemplateLanguages textSize={textSize} />
          </Box>
        </Cell>
      </Layout>
    </Box>
  );
};

const TemplateLanguages: FC<{
  textSize: IndustryAndLanguageProps['textSize'];
}> = ({ textSize }) => {
  const {
    languages: supportedLanguages,
    template: { language, languages },
  } = useTemplateContext();
  const { t } = useTranslation();

  const { experiments } = useExperiments({ readOnly: true });
  const isTemplateListingMultiLangEnabled = experiments.enabled(
    ENABLE_TEMPLATES_MULTI_LANGUAGE_SPEC,
  );

  if (isTemplateListingMultiLangEnabled) {
    return (
      <Text>
        {languages.map((id, index) => {
          return (
            <>
              <Text size={textSize}>
                {_.get(_.find(supportedLanguages, { id }), 'value', '')}
              </Text>
              {index !== languages.length - 1 && (
                <Text size={textSize}>
                  {t('marketplace.templates-page.about-section-industry-comma')}
                  &nbsp;
                </Text>
              )}
            </>
          );
        })}
      </Text>
    );
  }

  return (
    <Text size={textSize}>
      {_.get(_.find(supportedLanguages, { id: language }), 'value', '')}
    </Text>
  );
};

export default IndustryAndLanguage;
